import React, { useState } from 'react'
import {
  Box,
  Typography,
  Chip,
  Menu,
  MenuItem,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import SwapHoriz from '@mui/icons-material/SwapHoriz'
import Home from '@mui/icons-material/Home'
import Person from '@mui/icons-material/Person'
import Business from '@mui/icons-material/Business'
import LocalHospital from '@mui/icons-material/LocalHospital'
import TvIcon from '@mui/icons-material/Tv'
import Work from '@mui/icons-material/Work'
import OpenInNew from '@mui/icons-material/OpenInNew'
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp'
import { useNavigate } from 'react-router'
import { styled } from '@mui/material/styles'
import useServerAuth from 'src/customHooks/useServerAuth'
import { getUserRoles } from 'src/mentor/layout/utils'
import { PORTAL_CONFIG } from 'src/shared/config/portalConfig'

const FooterContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.85) 0%, rgba(99, 102, 241, 0.85) 100%)',
  backdropFilter: 'blur(24px) saturate(200%)',
  WebkitBackdropFilter: 'blur(24px) saturate(200%)',
  borderTop: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
  padding: theme.spacing(0.75, 2.5),
  minHeight: '48px',
  display: 'flex',
  alignItems: 'center',
  marginTop: 'auto', // Push to bottom of container
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.6, 1.5),
    minHeight: '44px',
    // On mobile, make it less prominent
    background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.75) 0%, rgba(99, 102, 241, 0.75) 100%)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
  },
}))

const FooterContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  maxWidth: '100%',
  gap: theme.spacing(1),
  minWidth: 0, // Allow shrinking
  [theme.breakpoints.down('md')]: {
    flexDirection: 'row',
    gap: theme.spacing(0.75),
    alignItems: 'center',
  },
}))

const RolesContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'nowrap', // Prevent wrapping to keep button visible
  flex: '1 1 auto',
  minWidth: 0, // Allow shrinking below content size
  overflowX: 'auto', // Allow horizontal scroll if needed
  overflowY: 'hidden',
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': {
    height: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '2px',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.4)',
    },
  },
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.5),
    // On mobile, make roles less prominent
    '& .MuiChip-root': {
      fontSize: '9px',
      height: '20px',
      padding: theme.spacing(0.1, 0.5),
      flexShrink: 0, // Prevent chips from shrinking
    },
  },
}))

const RoleChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: 'white',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  fontWeight: 500,
  fontSize: '11px',
  height: '24px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '9px',
    height: '20px',
    padding: theme.spacing(0.1, 0.5),
    // Make chips less prominent on mobile
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.25)',
  },
}))

const PortalSwitchButton = styled(IconButton)(({ theme }) => ({
  color: 'white',
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  padding: theme.spacing(0.75),
  width: '36px',
  height: '36px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.2s ease',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.6),
    width: '32px',
    height: '32px',
  },
}))

const PortalMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    background: 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(24px) saturate(180%)',
    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
    border: '1px solid rgba(67, 56, 202, 0.15)',
    borderRadius: '16px',
    boxShadow: '0 -12px 40px rgba(67, 56, 202, 0.25), 0 4px 12px rgba(0, 0, 0, 0.1)',
    minWidth: '280px',
    maxWidth: '320px',
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(0.5, 0),
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      bottom: -6,
      right: 20,
      width: 12,
      height: 12,
      background: 'rgba(255, 255, 255, 0.98)',
      borderRight: '1px solid rgba(67, 56, 202, 0.15)',
      borderBottom: '1px solid rgba(67, 56, 202, 0.15)',
      transform: 'translateY(50%) rotate(45deg)',
      zIndex: 0,
    },
  },
  '& .MuiMenuItem-root': {
    fontFamily: "'Inter', sans-serif",
    fontSize: '14px',
    padding: theme.spacing(1.25, 2),
    borderRadius: '8px',
    margin: theme.spacing(0.25, 1),
    transition: 'all 0.2s ease',
    position: 'relative',
    '&:hover': {
      backgroundColor: 'rgba(67, 56, 202, 0.06)',
      transform: 'translateX(2px)',
    },
    '&.Mui-disabled': {
      opacity: 1,
    },
    '&.active': {
      background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.12) 0%, rgba(99, 102, 241, 0.12) 100%)',
      borderLeft: '3px solid rgba(67, 56, 202, 0.8)',
      fontWeight: 600,
      color: '#4338ca',
      '&:hover': {
        backgroundColor: 'rgba(67, 56, 202, 0.15)',
        transform: 'translateX(0)',
      },
      '& .MuiSvgIcon-root': {
        color: '#4338ca',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '3px',
        background: 'linear-gradient(180deg, rgba(67, 56, 202, 0.8) 0%, rgba(99, 102, 241, 0.8) 100%)',
        borderRadius: '0 2px 2px 0',
      },
    },
  },
}))

// Role display names mapping
const ROLE_DISPLAY_NAMES = {
  ROLE_THERAPY_SUP_L1: 'Therapy Support L1',
  ROLE_THERAPY_SUP_L2: 'Therapy Support L2',
  ROLE_THERAPY_SUP_L3: 'Therapy Support L3',
  ROLE_CLINIC_SUCCESS_MANAGER: 'Clinic Success Manager',
  ROLE_CLINIC_SUCCESS_ADMIN: 'Clinic Success Admin',
  ROLE_CLINIC_ROSTER_MANAGER: 'Clinic Roster Manager',
  ROLE_CLINIC_ROSTER_ADMIN: 'Clinic Roster Admin',
  ROLE_SUPPORT: 'Support',
  ROLE_SUPPORT_USER: 'Support User',
  ROLE_PAYMENT_SUPPORT: 'Payment Support',
  ROLE_MANAGEMENT: 'Management',
  ROLE_VC_AUDIT_ADMIN: 'VC Audit Admin',
  ROLE_VC_AUDIT_MANAGER: 'VC Audit Manager',
}

// Portal configuration is now imported from centralized config (see imports above)

export default function SupportFooter() {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { allRolesInBoolean } = useServerAuth()
  const userRoles = getUserRoles()

  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  // Get support roles for display
  const supportRoles = userRoles.filter((role) =>
    Object.keys(ROLE_DISPLAY_NAMES).includes(role),
  )

  // Get available portals based on user roles
  const availablePortals = PORTAL_CONFIG.filter((portal) =>
    portal.roles.some((role) => userRoles.includes(role)),
  )

  const handlePortalMenuClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePortalMenuClose = () => {
    setAnchorEl(null)
  }

  const handlePortalSwitch = (portal) => {
    handlePortalMenuClose()
    
    // Small delay to ensure menu closes before navigation
    setTimeout(() => {
      if (portal.external) {
        window.open(portal.path, '_blank', 'noopener,noreferrer')
      } else if (portal.path === '/') {
        // For Wellness App (consumer portal), use full page navigation to switch contexts
        // Set flag to indicate user explicitly chose consumer portal - prevents auto-redirect back
        sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
        sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')
        // Clear the auto-navigation flag to prevent redirect back to support portal
        sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
        // Use window.location.replace to avoid adding to history and prevent back button issues
        window.location.replace('/')
      } else {
        navigate(portal.path)
      }
    }, 100)
  }

  const isCurrentPortal = (portal) => {
    if (portal.external) return false
    
    const currentPath = window.location.pathname
    
    // Special handling for root path (consumer portal)
    if (portal.path === '/') {
      // Only match if we're exactly at root or at a consumer route (not other portals)
      return (
        currentPath === '/' ||
        (!currentPath.startsWith('/support-home') &&
          !currentPath.startsWith('/mentor-home') &&
          !currentPath.startsWith('/clinic-home') &&
          !currentPath.startsWith('/ca-home') &&
          !currentPath.startsWith('/kiosk') &&
          !currentPath.startsWith('/workplace'))
      )
    }
    
    // For other portals, check if path starts with portal path followed by / or end of string
    return currentPath === portal.path || currentPath.startsWith(portal.path + '/')
  }

  return (
    <FooterContainer className="no-print">
      <FooterContent>
        <RolesContainer>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: 500,
              fontSize: isMobile ? '9px' : '11px',
              marginRight: theme.spacing(0.75),
              display: { xs: 'none', sm: 'block' }, // Hide "Roles:" label on mobile to save space
            }}
          >
            Roles:
          </Typography>
          {supportRoles.length > 0 ? (
            supportRoles.map((role) => (
              <RoleChip
                key={role}
                label={ROLE_DISPLAY_NAMES[role] || role.replace('ROLE_', '').replace(/_/g, ' ')}
                size="small"
              />
            ))
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: isMobile ? '11px' : '12px',
              }}
            >
              No support roles assigned
            </Typography>
          )}
        </RolesContainer>

        {availablePortals.length > 1 && (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              marginLeft: 'auto',
              flexShrink: 0,
              minWidth: 'fit-content', // Ensure button area doesn't shrink
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 500,
                fontSize: isMobile ? '10px' : '11px',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Switch Portal:
            </Typography>
            <PortalSwitchButton
              onClick={handlePortalMenuClick}
              aria-label="Switch portal"
              size="small"
            >
              <SwapHoriz sx={{ fontSize: isMobile ? 18 : 20 }} />
            </PortalSwitchButton>

            <PortalMenu
              anchorEl={anchorEl}
              open={open}
              onClose={handlePortalMenuClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: isMobile ? 'center' : 'right',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: isMobile ? 'center' : 'right',
              }}
              PaperProps={{
                sx: {
                  maxHeight: isMobile ? '60vh' : '70vh',
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '4px',
                    '&:hover': {
                      background: 'rgba(0, 0, 0, 0.3)',
                    },
                  },
                },
              }}
            >
              {availablePortals.map((portal) => {
                const Icon = portal.icon
                const isActive = isCurrentPortal(portal)
                // Don't disable Wellness App even if it's detected as active (shouldn't happen from support portal)
                const shouldDisable = isActive && !portal.external && portal.path !== '/'
                return (
                  <MenuItem
                    key={portal.id}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handlePortalSwitch(portal)
                    }}
                    className={isActive ? 'active' : ''}
                    disabled={shouldDisable}
                    sx={{
                      '&.active': {
                        '& .portal-icon': {
                          color: '#4338ca',
                        },
                        '& .portal-name': {
                          color: '#4338ca',
                          fontWeight: 600,
                        },
                        '& .portal-badge': {
                          background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
                          color: '#4338ca',
                          border: '1px solid rgba(67, 56, 202, 0.3)',
                        },
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5} width="100%">
                      <Icon
                        className="portal-icon"
                        sx={{
                          fontSize: 20,
                          color: isActive ? '#4338ca' : 'rgba(0, 0, 0, 0.6)',
                          transition: 'color 0.2s ease',
                        }}
                      />
                      <Box flex={1}>
                        <Typography
                          className="portal-name"
                          variant="body2"
                          fontWeight={isActive ? 600 : 500}
                          sx={{
                            color: isActive ? '#4338ca' : 'rgba(0, 0, 0, 0.87)',
                            transition: 'color 0.2s ease',
                          }}
                        >
                          {portal.name}
                        </Typography>
                      </Box>
                      {isActive && (
                        <Chip
                          className="portal-badge"
                          label="Current"
                          size="small"
                          sx={{
                            height: '20px',
                            fontSize: '10px',
                            fontWeight: 600,
                            background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
                            color: '#4338ca',
                            border: '1px solid rgba(67, 56, 202, 0.3)',
                            '& .MuiChip-label': {
                              padding: '0 6px',
                            },
                          }}
                        />
                      )}
                      {portal.external && (
                        <OpenInNew
                          sx={{
                            fontSize: 16,
                            color: isActive ? '#4338ca' : 'rgba(0, 0, 0, 0.5)',
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                )
              })}
            </PortalMenu>
          </Box>
        )}
      </FooterContent>
    </FooterContainer>
  )
}
